<template>
    <Modal ref="modal" :title="modalTitle" size="large">
        <!-- Tabs Header -->
        <div class="border-b border-gray-200 dark:border-gray-700 mb-5">
            <ul class="flex flex-wrap -mb-px text-sm font-medium text-center">
                <li class="mr-2">
                    <button 
                        @click="activeTab = 'share'" 
                        class="inline-flex items-center gap-2 p-3 border-b-2 rounded-t-lg transition-colors cursor-pointer text-xs md:text-sm"
                        :class="[
                            activeTab === 'share' 
                                ? 'text-indigo-600 border-indigo-600 dark:text-indigo-500 dark:border-indigo-500 font-semibold' 
                                : 'text-gray-500 border-transparent hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        ]"
                    >
                        <ShareIcon class="w-4 h-4" />
                        {{ $t('resourceShareModal.shareOptionsTab') }}
                    </button>
                </li>
                <li>
                    <button 
                        @click="activeTab = 'manage'; fetchSharedEntities()" 
                        class="inline-flex items-center gap-2 p-3 border-b-2 rounded-t-lg transition-colors cursor-pointer text-xs md:text-sm"
                        :class="[
                            activeTab === 'manage' 
                                ? 'text-indigo-600 border-indigo-600 dark:text-indigo-500 dark:border-indigo-500 font-semibold' 
                                : 'text-gray-500 border-transparent hover:text-gray-600 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        ]"
                    >
                        <UserGroupIcon class="w-4 h-4" />
                        {{ $t('resourceShareModal.peopleWithAccessTab', { count: sharedEntities.length }) }}
                    </button>
                </li>
            </ul>
        </div>

        <!-- Tab 1: Share Options -->
        <div v-show="activeTab === 'share'" class="space-y-6">
            <!-- Resource Info Header -->
            <div class="p-4 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700/60 flex items-center justify-between">
                <div class="flex items-center space-x-3">
                    <div class="p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                        <component :is="resourceType === 'artist' ? UserGroupIcon : MusicalNoteIcon" class="w-6 h-6" />
                    </div>
                    <div>
                        <h4 class="text-sm font-bold text-gray-900 dark:text-white">{{ resourceName }}</h4>
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {{ resourceType === 'artist' ? $t('resourceShareModal.artistCatalog') : $t('resourceShareModal.songChart') }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Permission Scope Selection (When sharing Song) -->
            <div v-if="resourceType === 'song' && artistId" class="space-y-2">
                <label class="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {{ $t('resourceShareModal.sharingScope') }}
                </label>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label 
                        @click="shareScope = 'single'"
                        :class="[
                            shareScope === 'single'
                                ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/20 dark:border-indigo-500 text-indigo-900 dark:text-indigo-200'
                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        ]"
                        class="relative flex cursor-pointer rounded-xl border p-3.5 shadow-sm focus:outline-none transition-all"
                    >
                        <input type="radio" name="share-scope" value="single" v-model="shareScope" class="sr-only" />
                        <span class="flex flex-1">
                            <span class="flex flex-col">
                                <span class="block text-sm font-semibold">{{ $t('resourceShareModal.onlyThisSong') }}</span>
                                <span class="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                                    {{ $t('resourceShareModal.onlyThisSongDesc', { name: resourceName }) }}
                                </span>
                            </span>
                        </span>
                        <CheckCircleIcon :class="shareScope === 'single' ? 'text-indigo-600 dark:text-indigo-400' : 'invisible'" class="h-5 w-5" />
                    </label>

                    <label 
                        @click="shareScope = 'artist'"
                        :class="[
                            shareScope === 'artist'
                                ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/20 dark:border-indigo-500 text-indigo-900 dark:text-indigo-200'
                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        ]"
                        class="relative flex cursor-pointer rounded-xl border p-3.5 shadow-sm focus:outline-none transition-all"
                    >
                        <input type="radio" name="share-scope" value="artist" v-model="shareScope" class="sr-only" />
                        <span class="flex flex-1">
                            <span class="flex flex-col">
                                <span class="block text-sm font-semibold">{{ $t('resourceShareModal.entireArtistCatalog') }}</span>
                                <span class="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                                    {{ $t('resourceShareModal.entireArtistCatalogDesc') }}
                                </span>
                            </span>
                        </span>
                        <CheckCircleIcon :class="shareScope === 'artist' ? 'text-indigo-600 dark:text-indigo-400' : 'invisible'" class="h-5 w-5" />
                    </label>
                </div>
            </div>

            <!-- Permission Role Selection (Viewer vs Editor) -->
            <div class="space-y-2">
                <label class="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {{ $t('resourceShareModal.accessPermission') }}
                </label>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label 
                        @click="selectedRole = 'viewer'"
                        :class="[
                            selectedRole === 'viewer'
                                ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/20 dark:border-indigo-500 text-indigo-900 dark:text-indigo-200'
                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        ]"
                        class="relative flex cursor-pointer rounded-xl border p-3.5 shadow-sm focus:outline-none transition-all"
                    >
                        <input type="radio" name="selected-role" value="viewer" v-model="selectedRole" class="sr-only" />
                        <span class="flex flex-1">
                            <span class="flex flex-col">
                                <span class="block text-sm font-semibold">{{ $t('resourceShareModal.viewerRole') }}</span>
                                <span class="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                                    {{ $t('resourceShareModal.viewerRoleDesc') }}
                                </span>
                            </span>
                        </span>
                        <CheckCircleIcon :class="selectedRole === 'viewer' ? 'text-indigo-600 dark:text-indigo-400' : 'invisible'" class="h-5 w-5" />
                    </label>

                    <label 
                        @click="selectedRole = 'editor'"
                        :class="[
                            selectedRole === 'editor'
                                ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/20 dark:border-indigo-500 text-indigo-900 dark:text-indigo-200'
                                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                        ]"
                        class="relative flex cursor-pointer rounded-xl border p-3.5 shadow-sm focus:outline-none transition-all"
                    >
                        <input type="radio" name="selected-role" value="editor" v-model="selectedRole" class="sr-only" />
                        <span class="flex flex-1">
                            <span class="flex flex-col">
                                <span class="block text-sm font-semibold">{{ $t('resourceShareModal.editorRole') }}</span>
                                <span class="mt-1 flex items-center text-xs text-gray-500 dark:text-gray-400">
                                    {{ $t('resourceShareModal.editorRoleDesc') }}
                                </span>
                            </span>
                        </span>
                        <CheckCircleIcon :class="selectedRole === 'editor' ? 'text-indigo-600 dark:text-indigo-400' : 'invisible'" class="h-5 w-5" />
                    </label>
                </div>
            </div>

            <!-- Main Sharing Area -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-200 dark:divide-gray-700">
                <!-- Left: Contact Search & Grant -->
                <div class="space-y-4">
                    <div>
                        <label class="block mb-2 text-sm font-semibold text-gray-900 dark:text-white">
                            {{ $t('resourceShareModal.shareWithMusicianOrTeam') }}
                        </label>
                        <div class="relative">
                            <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                                <MagnifyingGlassIcon class="w-4 h-4 text-gray-400" />
                            </div>
                            <input 
                                type="search" 
                                class="block w-full p-2.5 ps-10 text-sm text-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:placeholder-gray-400 dark:text-white" 
                                :placeholder="$t('resourceShareModal.searchPlaceholder')" 
                                v-model="searchText"
                            />
                        </div>
                    </div>

                    <!-- Search Results -->
                    <div class="min-h-[140px] max-h-[220px] overflow-y-auto space-y-2 pr-1">
                        <div v-if="loadingContacts" class="py-4 text-center">
                            <Loading class="w-full" />
                        </div>
                        <div v-else-if="contacts.length === 0" class="text-xs text-gray-500 dark:text-gray-400 text-center py-6">
                            {{ searchText.length < 2 ? $t('resourceShareModal.searchHint') : $t('resourceShareModal.noContactsFound') }}
                        </div>
                        <div 
                            v-else 
                            v-for="contact in contacts" 
                            :key="contact._id || contact.id"
                            class="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700/60 rounded-xl"
                        >
                            <div class="flex items-center space-x-3 min-w-0">
                                <div class="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300 flex items-center justify-center font-bold text-xs">
                                    {{ getInitials(contact.name || contact.username || 'U') }}
                                </div>
                                <div class="min-w-0">
                                    <p class="text-xs font-semibold text-gray-900 dark:text-white truncate">{{ contact.name || contact.username }}</p>
                                    <p class="text-[11px] text-gray-500 dark:text-gray-400 truncate">{{ contact.email || (contact.isTeam ? $t('resourceShareModal.sharedWithTeam') : '') }}</p>
                                </div>
                            </div>
                            <div v-if="getContactShareStatus(contact)" class="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800 dark:text-emerald-300 rounded-lg shadow-sm">
                                <CheckCircleIcon class="w-3.5 h-3.5" />
                                <span>{{ $t('resourceShareModal.alreadyShared') }}</span>
                            </div>
                            <button 
                                v-else
                                @click="grantAccess(contact)"
                                :disabled="sharingInProgress"
                                type="button" 
                                class="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
                            >
                                {{ $t('resourceShareModal.grantButton', { role: selectedRole === 'editor' ? $t('resourceShareModal.editRoleShort') : $t('resourceShareModal.viewRoleShort') }) }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Right: Secure Direct Link -->
                <div class="md:ps-8 pt-6 md:pt-0 space-y-4">
                    <h5 class="text-sm font-semibold text-gray-900 dark:text-white">
                        {{ $t('resourceShareModal.directSecureLink') }}
                    </h5>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                        {{ $t('resourceShareModal.directLinkDesc', {
                            type: resourceType === 'artist' ? $t('resourceShareModal.artistCatalog') : $t('resourceShareModal.songChart'),
                            role: selectedRole === 'editor' ? $t('resourceShareModal.editorRole') : $t('resourceShareModal.viewerRole')
                        }) }}
                    </p>

                    <div class="p-3 bg-gray-50 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700/60 space-y-3">
                        <div class="flex items-center justify-between">
                            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {{ $t('resourceShareModal.inviteLink', { role: selectedRole === 'editor' ? $t('resourceShareModal.editorRole') : $t('resourceShareModal.viewerRole') }) }}
                            </span>
                            <button 
                                @click="generateLink"
                                type="button" 
                                class="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                            >
                                {{ $t('resourceShareModal.generateNew') }}
                            </button>
                        </div>
                        <div class="flex items-center space-x-2">
                            <input 
                                type="text" 
                                readonly 
                                :value="shareLink" 
                                :placeholder="$t('resourceShareModal.linkPlaceholder')" 
                                class="block w-full p-2 text-xs text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none"
                            />
                            <button 
                                @click="copyShareLink"
                                :disabled="!shareLink"
                                type="button" 
                                class="p-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40"
                            >
                                <ClipboardDocumentIcon class="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tab 2: Manage Access & Revoke Permissions -->
        <div v-show="activeTab === 'manage'" class="space-y-4">
            <div class="flex items-center justify-between">
                <div>
                    <h4 class="text-sm font-bold text-gray-900 dark:text-white">{{ $t('resourceShareModal.manageTitle') }}</h4>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ $t('resourceShareModal.manageDesc') }}</p>
                </div>
                <button 
                    @click="fetchSharedEntities" 
                    type="button" 
                    class="p-1.5 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 rounded-lg transition-colors"
                >
                    <ArrowPathIcon class="w-4 h-4" :class="{ 'animate-spin': loadingShares }" />
                </button>
            </div>

            <div v-if="loadingShares" class="py-12 text-center">
                <Loading class="w-full" />
            </div>

            <div v-else-if="sharedEntities.length === 0" class="py-12 text-center text-gray-500 dark:text-gray-400">
                <UserGroupIcon class="w-12 h-12 mx-auto text-gray-400 mb-2 opacity-60" />
                <p class="text-sm font-medium">{{ $t('resourceShareModal.noShares') }}</p>
                <p class="text-xs text-gray-400 mt-1">{{ $t('resourceShareModal.noSharesDesc') }}</p>
            </div>

            <div v-else class="max-h-[350px] overflow-y-auto space-y-2.5 pr-1">
                <div 
                    v-for="entity in sharedEntities" 
                    :key="entity.id"
                    class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700/60 rounded-xl"
                >
                    <div class="flex items-center space-x-3 min-w-0">
                        <div 
                            class="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs"
                            :class="entity.isTeam ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300'"
                        >
                            {{ entity.isTeam ? 'TEAM' : getInitials(entity.name) }}
                        </div>
                        <div class="min-w-0">
                            <div class="flex items-center gap-2">
                                <p class="text-xs font-semibold text-gray-900 dark:text-white truncate">{{ entity.name }}</p>
                                <span 
                                    class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                    :class="entity.role === 'editor' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'"
                                >
                                    {{ entity.role === 'editor' ? $t('resourceShareModal.editRoleShort') : $t('resourceShareModal.viewRoleShort') }}
                                </span>
                                <span 
                                    v-if="resourceType === 'song'"
                                    class="text-[10px] font-medium px-2 py-0.5 rounded-full"
                                    :class="entity.resourceType === 'artist' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'"
                                >
                                    {{ entity.resourceType === 'artist' ? $t('resourceShareModal.artistScopeBadge') : $t('resourceShareModal.songScopeBadge') }}
                                </span>
                            </div>
                            <p class="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                {{ entity.email || (entity.isTeam ? $t('resourceShareModal.sharedWithTeam') : '') }}
                                <span v-if="entity.sharedBy" class="text-gray-400"> • {{ $t('resourceShareModal.sharedBy', { name: entity.sharedBy }) }}</span>
                            </p>
                        </div>
                    </div>

                    <div class="flex items-center space-x-2">
                        <button 
                            @click="revokeAccess(entity)"
                            :disabled="revokingId === entity.id"
                            type="button" 
                            class="px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 border border-red-200 dark:border-red-900/60 rounded-lg transition-colors disabled:opacity-50"
                        >
                            <span v-if="revokingId === entity.id">{{ $t('resourceShareModal.revoking') }}</span>
                            <span v-else>{{ $t('resourceShareModal.revoke') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script setup>
import { ref, computed, watch, defineExpose } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/components/common/TriadModal.vue';
import Loading from '@/components/TriadLoad.vue';
import { 
    MusicalNoteIcon, 
    UserGroupIcon, 
    ClipboardDocumentIcon, 
    CheckCircleIcon, 
    MagnifyingGlassIcon,
    ShareIcon,
    ArrowPathIcon
} from '@heroicons/vue/24/outline';
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import { getPublicWebBaseUrl } from '@/utils/capacitor';

const { t } = useI18n();

const modal = ref(null);
const activeTab = ref('share'); // 'share' | 'manage'
const resourceType = ref('song'); // 'song' | 'artist'
const resourceId = ref('');
const resourceName = ref('');
const artistId = ref(null);
const shareScope = ref('single'); // 'single' | 'artist'
const selectedRole = ref('viewer'); // 'viewer' | 'editor'
const searchText = ref('');
const contacts = ref([]);
const loadingContacts = ref(false);
const sharingInProgress = ref(false);
const shareLink = ref('');
const sharedEntities = ref([]);
const loadingShares = ref(false);
const revokingId = ref(null);
let debounceTimer = null;

const modalTitle = computed(() => {
    return resourceType.value === 'artist' 
        ? t('resourceShareModal.shareArtistTitle') 
        : t('resourceShareModal.shareSongTitle');
});

const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

const open = async (type, id, name, relArtistId = null) => {
    activeTab.value = 'share';
    resourceType.value = type;
    resourceId.value = id;
    resourceName.value = name;
    artistId.value = relArtistId;
    shareScope.value = 'single';
    selectedRole.value = 'viewer';
    searchText.value = '';
    contacts.value = [];
    sharedEntities.value = [];
    shareLink.value = '';

    if (modal.value) {
        modal.value.open();
    }
    await Promise.all([
        fetchContacts(''),
        fetchSharedEntities()
    ]);
};

const fetchContacts = async (query = '') => {
    loadingContacts.value = true;
    try {
        const endpoint = query && query.length >= 2 
            ? `/api/members/contacts?term=${encodeURIComponent(query)}` 
            : '/api/members/contacts';
        const res = await fetch(endpoint);
        if (res.ok) {
            const data = await res.json();
            contacts.value = Array.isArray(data) ? data : (data.contacts || []);
        }
    } catch (e) {
        console.error('Failed to load contacts', e);
    } finally {
        loadingContacts.value = false;
    }
};

const fetchSharedEntities = async () => {
    if (!resourceId.value) return;
    loadingShares.value = true;
    try {
        const promises = [
            fetch(`/api/shares/resource?type=${resourceType.value}&id=${resourceId.value}`)
        ];
        if (resourceType.value === 'song' && artistId.value) {
            promises.push(fetch(`/api/shares/resource?type=artist&id=${artistId.value}`));
        }

        const responses = await Promise.all(promises);
        const lists = await Promise.all(responses.map(res => res.ok ? res.json() : []));
        
        const combined = [];
        const seenIds = new Set();
        for (const data of lists) {
            const list = Array.isArray(data) ? data : [];
            for (const item of list) {
                if (!item.share_token && !seenIds.has(item.id)) {
                    seenIds.add(item.id);
                    combined.push(item);
                }
            }
        }

        sharedEntities.value = combined.map(item => {
            const isTeam = !!item.grantee_team_id;
            const granteeId = item.grantee_user_id || item.grantee_user?.id || item.grantee_user?._id;
            const granteeTeamId = item.grantee_team_id || item.grantee_team?.id || item.grantee_team?._id;
            const name = isTeam 
                ? (item.grantee_team?.name || 'Team')
                : (item.grantee_user?.name || item.grantee_user?.username || 'User');
            const email = isTeam ? '' : (item.grantee_user?.email || '');
            const sharedBy = item.granter?.name || item.granter?.username || '';
            return {
                id: item.id,
                granteeId,
                granteeTeamId,
                role: item.role,
                resourceType: item.resource_type || (item.resource_trn?.includes('artist') ? 'artist' : 'song'),
                isTeam,
                name,
                email,
                sharedBy
            };
        });
    } catch (e) {
        console.error('Failed to load shared entities', e);
    } finally {
        loadingShares.value = false;
    }
};

const getContactShareStatus = (contact) => {
    const contactId = String(contact._id || contact.id || '');
    if (!contactId || !sharedEntities.value.length) return null;

    return sharedEntities.value.find(share => {
        if (contact.isTeam) {
            return String(share.granteeTeamId || '') === contactId;
        } else {
            return String(share.granteeId || '') === contactId;
        }
    }) || null;
};

watch(searchText, (val) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!val || val.length < 2) {
        fetchContacts('');
        return;
    }
    debounceTimer = setTimeout(() => {
        fetchContacts(val);
    }, 300);
});

const grantAccess = async (contact) => {
    sharingInProgress.value = true;
    try {
        const isTargetArtist = resourceType.value === 'artist' || shareScope.value === 'artist';
        const targetId = isTargetArtist ? (resourceType.value === 'artist' ? resourceId.value : artistId.value) : resourceId.value;
        const endpoint = isTargetArtist ? `/api/artist/${targetId}/share` : `/api/song/${targetId}/share`;

        const payload = {
            userId: contact.isTeam ? undefined : (contact._id || contact.id),
            teamId: contact.isTeam ? (contact._id || contact.id) : undefined,
            role: selectedRole.value
        };

        const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (res.ok) {
            toastStore.dispatch('addMessage', {
                message: isTargetArtist ? t('resourceShareModal.artistSharedSuccess') : t('resourceShareModal.songSharedSuccess'),
                icon: 'success'
            });
            await fetchSharedEntities();
        } else {
            toastStore.dispatch('addMessage', { message: t('resourceShareModal.shareFailed'), icon: 'error' });
        }
    } catch (err) {
        toastStore.dispatch('addMessage', { message: t('resourceShareModal.shareError'), icon: 'error' });
    } finally {
        sharingInProgress.value = false;
    }
};

const revokeAccess = async (entity) => {
    const confirmed = await dialogStore.dispatch('confirm', t('resourceShareModal.revokeConfirm', { name: entity.name }));
    if (!confirmed) return;

    revokingId.value = entity.id;
    try {
        const res = await fetch(`/api/resource/${entity.id}`, {
            method: 'DELETE'
        });

        if (res.ok) {
            toastStore.dispatch('addMessage', {
                message: t('resourceShareModal.accessRevokedSuccess', { name: entity.name }),
                icon: 'success'
            });
            sharedEntities.value = sharedEntities.value.filter(e => e.id !== entity.id);
        } else {
            toastStore.dispatch('addMessage', { message: t('resourceShareModal.accessRevokedFailed'), icon: 'error' });
        }
    } catch (e) {
        console.error('Error revoking share', e);
        toastStore.dispatch('addMessage', { message: t('resourceShareModal.accessRevokedError'), icon: 'error' });
    } finally {
        revokingId.value = null;
    }
};

const generateLink = async () => {
    // No app nativo, window.location.origin é capacitor://localhost — usa a
    // base pública (backend) para o link ser compartilhável.
    const baseUrl = getPublicWebBaseUrl();
    try {
        const isTargetArtist = resourceType.value === 'artist' || shareScope.value === 'artist';
        const targetId = isTargetArtist ? (resourceType.value === 'artist' ? resourceId.value : artistId.value) : resourceId.value;
        const type = isTargetArtist ? 'artist' : 'song';

        const res = await fetch(`/api/shares/link?type=${type}&id=${targetId}&role=${selectedRole.value}`);
        if (res.ok) {
            const data = await res.json();
            shareLink.value = baseUrl + data.share_url;
        } else {
            shareLink.value = `${baseUrl}/share/${type}/${targetId}`;
        }
    } catch {
        shareLink.value = `${baseUrl}/share/${resourceType.value}/${resourceId.value}`;
    }
};

const copyShareLink = async () => {
    if (!shareLink.value) return;
    try {
        await navigator.clipboard.writeText(shareLink.value);
        toastStore.dispatch('addMessage', { message: t('resourceShareModal.linkCopied'), icon: 'success' });
    } catch (e) {
        console.error(e);
    }
};

defineExpose({
    open
});
</script>
